import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Upload, Package, CheckCircle, XCircle, Edit2, Trash2, Image, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Decks', 'Trucks', 'Wheels', 'Obstacles', 'Accessories', 'Other'];

export default function AdminProductImport() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [rawText, setRawText] = useState('');
  const [parsedProducts, setParsedProducts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const parseProducts = () => {
    if (!rawText.trim()) {
      toast.error('Please paste product data');
      return;
    }

    const lines = rawText.trim().split('\n').filter(line => line.trim());
    const products = [];

    for (const line of lines) {
      // Try to parse various formats:
      // Format 1: "Product Name - $50.00" or "Product Name $50"
      // Format 2: "Product Name, $50.00, Category"
      // Format 3: "Product Name | 50 | Decks"
      // Format 4: Just "Product Name" (price defaults to 0)
      
      let name = '';
      let price = '0';
      let category = 'Other';
      let description = '';

      // Try pipe format first
      if (line.includes('|')) {
        const parts = line.split('|').map(p => p.trim());
        name = parts[0] || '';
        price = parts[1]?.replace(/[$,]/g, '') || '0';
        category = parts[2] || 'Other';
        description = parts[3] || '';
      }
      // Try comma format
      else if (line.includes(',')) {
        const parts = line.split(',').map(p => p.trim());
        name = parts[0] || '';
        price = parts[1]?.replace(/[$,]/g, '') || '0';
        category = parts[2] || 'Other';
        description = parts[3] || '';
      }
      // Try dash format with price
      else if (line.includes(' - $')) {
        const match = line.match(/^(.+?)\s*-\s*\$?([\d.]+)/);
        if (match) {
          name = match[1].trim();
          price = match[2];
        }
      }
      // Try to find price anywhere in line
      else {
        const priceMatch = line.match(/\$?([\d]+(?:\.[\d]{2})?)/);
        if (priceMatch) {
          price = priceMatch[1];
          name = line.replace(priceMatch[0], '').replace(/[-|,]/g, '').trim();
        } else {
          name = line.trim();
        }
      }

      // Validate category
      if (!CATEGORIES.includes(category)) {
        category = 'Other';
      }

      if (name) {
        products.push({
          id: Date.now() + Math.random(),
          name,
          price: parseFloat(price) || 0,
          category,
          description: description || `${name} - Add description`,
          stock: 1,
          image_url: '',
          valid: true
        });
      }
    }

    if (products.length === 0) {
      toast.error('Could not parse any products');
      return;
    }

    setParsedProducts(products);
    toast.success(`Parsed ${products.length} products`);
  };

  const updateProduct = (id, field, value) => {
    setParsedProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const removeProduct = (id) => {
    setParsedProducts(prev => prev.filter(p => p.id !== id));
  };

  const handleImport = async () => {
    const validProducts = parsedProducts.filter(p => p.valid && p.name && p.price > 0);
    
    if (validProducts.length === 0) {
      toast.error('No valid products to import');
      return;
    }

    setImporting(true);
    const results = { success: 0, failed: 0, errors: [] };

    for (const product of validProducts) {
      try {
        await axios.post(`${API}/admin/products`, {
          name: product.name,
          description: product.description,
          price: parseFloat(product.price),
          category: product.category,
          stock: parseInt(product.stock) || 1,
          image_url: product.image_url || ''
        }, { headers: getAuthHeader() });
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`${product.name}: ${error.response?.data?.detail || 'Failed'}`);
      }
    }

    setImportResults(results);
    setImporting(false);

    if (results.success > 0) {
      toast.success(`Imported ${results.success} products`);
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} products failed`);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin" 
            className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO ADMIN
          </Link>
          <h1 className="text-3xl font-sans font-bold text-white flex items-center gap-3">
            <Upload className="w-8 h-8 text-tiffany-500" />
            BULK PRODUCT IMPORT
          </h1>
          <p className="text-zinc-500 font-mono text-sm mt-1">
            Paste product data to create multiple listings at once
          </p>
        </div>

        {/* Import Results */}
        {importResults && (
          <div className="mb-6 p-4 border border-zinc-800 bg-zinc-900">
            <h3 className="font-mono text-white mb-2">IMPORT COMPLETE</h3>
            <div className="flex gap-4 mb-2">
              <span className="text-green-500 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> {importResults.success} created
              </span>
              {importResults.failed > 0 && (
                <span className="text-red-500 flex items-center gap-1">
                  <XCircle className="w-4 h-4" /> {importResults.failed} failed
                </span>
              )}
            </div>
            {importResults.errors.length > 0 && (
              <div className="text-xs text-red-400 mt-2">
                {importResults.errors.map((err, i) => <p key={i}>{err}</p>)}
              </div>
            )}
            <Button
              onClick={() => navigate('/admin')}
              className="mt-3 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
            >
              GO TO PRODUCTS
            </Button>
          </div>
        )}

        {/* Step 1: Paste Data */}
        {!importResults && (
          <>
            <div className="mb-6">
              <h2 className="font-mono text-lg text-white mb-3 flex items-center gap-2">
                <span className="bg-tiffany-500 text-black w-6 h-6 flex items-center justify-center text-sm">1</span>
                PASTE PRODUCT DATA
              </h2>
              <div className="border border-zinc-800 p-4 bg-zinc-900/50 mb-3">
                <p className="text-xs text-zinc-400 font-mono mb-2">SUPPORTED FORMATS:</p>
                <code className="text-xs text-tiffany-500 block mb-1">Product Name | Price | Category | Description</code>
                <code className="text-xs text-tiffany-500 block mb-1">Product Name, $50.00, Decks</code>
                <code className="text-xs text-tiffany-500 block mb-1">Product Name - $50</code>
                <code className="text-xs text-tiffany-500 block">Product Name (price defaults to $0)</code>
              </div>
              <Textarea
                placeholder="Paste your product list here (one product per line)..."
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none min-h-[200px]"
                data-testid="import-textarea"
              />
              <Button
                onClick={parseProducts}
                className="mt-3 bg-zinc-800 text-white hover:bg-zinc-700 rounded-none"
                data-testid="parse-btn"
              >
                PARSE PRODUCTS
              </Button>
            </div>

            {/* Step 2: Review & Edit */}
            {parsedProducts.length > 0 && (
              <div className="mb-6">
                <h2 className="font-mono text-lg text-white mb-3 flex items-center gap-2">
                  <span className="bg-tiffany-500 text-black w-6 h-6 flex items-center justify-center text-sm">2</span>
                  REVIEW & EDIT ({parsedProducts.length} PRODUCTS)
                </h2>
                <p className="text-xs text-zinc-500 font-mono mb-4">
                  Edit details below. Products without images will need photos added after import.
                </p>
                
                <div className="space-y-3">
                  {parsedProducts.map((product, idx) => (
                    <div 
                      key={product.id}
                      className="border border-zinc-800 p-4 bg-zinc-900/50"
                      data-testid={`product-row-${idx}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Image placeholder */}
                        <div className="w-16 h-16 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Image className="w-6 h-6 text-zinc-600" />
                          )}
                        </div>
                        
                        {/* Product details */}
                        <div className="flex-1 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-mono text-zinc-500">NAME</label>
                              <Input
                                value={product.name}
                                onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                                className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-mono text-zinc-500">PRICE ($)</label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={product.price}
                                  onChange={(e) => updateProduct(product.id, 'price', e.target.value)}
                                  className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] font-mono text-zinc-500">STOCK</label>
                                <Input
                                  type="number"
                                  value={product.stock}
                                  onChange={(e) => updateProduct(product.id, 'stock', e.target.value)}
                                  className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-mono text-zinc-500">CATEGORY</label>
                              <Select 
                                value={product.category}
                                onValueChange={(v) => updateProduct(product.id, 'category', v)}
                              >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                                  {CATEGORIES.map(cat => (
                                    <SelectItem key={cat} value={cat} className="text-white text-sm">{cat}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-[10px] font-mono text-zinc-500">IMAGE URL (optional)</label>
                              <Input
                                value={product.image_url}
                                onChange={(e) => updateProduct(product.id, 'image_url', e.target.value)}
                                placeholder="https://..."
                                className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-mono text-zinc-500">DESCRIPTION</label>
                            <Input
                              value={product.description}
                              onChange={(e) => updateProduct(product.id, 'description', e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-white text-sm rounded-none h-8"
                            />
                          </div>
                        </div>
                        
                        {/* Remove button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeProduct(product.id)}
                          className="text-zinc-500 hover:text-red-500 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Validation warning */}
                      {(!product.name || product.price <= 0) && (
                        <p className="text-xs text-yellow-500 mt-2">
                          Missing required fields (name and price &gt; 0)
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Step 3: Import */}
                <div className="mt-6">
                  <h2 className="font-mono text-lg text-white mb-3 flex items-center gap-2">
                    <span className="bg-tiffany-500 text-black w-6 h-6 flex items-center justify-center text-sm">3</span>
                    IMPORT PRODUCTS
                  </h2>
                  <p className="text-xs text-zinc-500 font-mono mb-4">
                    {parsedProducts.filter(p => p.name && p.price > 0).length} of {parsedProducts.length} products ready to import
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleImport}
                      disabled={importing || parsedProducts.filter(p => p.name && p.price > 0).length === 0}
                      className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
                      data-testid="import-btn"
                    >
                      {importing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          IMPORTING...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          IMPORT {parsedProducts.filter(p => p.name && p.price > 0).length} PRODUCTS
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setParsedProducts([]);
                        setRawText('');
                      }}
                      variant="outline"
                      className="rounded-none border-zinc-700 text-zinc-400"
                    >
                      CLEAR ALL
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
